import React, { useEffect } from "react";
import {
  Routes,
  Route,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { DeviceDetails } from "pages/devices/DeviceDetails";
import DevicesListPage from "pages/devices/DevicesList";
import { useAuth } from "context/AuthContext";
import {
  buildDeviceDetailsPath,
  isValidLinkId,
} from "utils/deviceNavigation";

// Wrapper component to extract query parameters and pass them to DeviceDetails
const DeviceDetailsWrapper: React.FC<{ isAdminView?: boolean }> = ({
  isAdminView = false,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const serialNumber = searchParams.get("serialNumber");
  const linkIdParam = searchParams.get("linkId");
  const linkId = isValidLinkId(linkIdParam) ? linkIdParam : undefined;
  const adminView = isAdminView || user?.role === "ADMIN";

  // Normalize legacy URLs like ?linkId=undefined and send admins to /all-devices
  useEffect(() => {
    if (!serialNumber) return;

    const desiredPath = adminView ? "/all-devices/details" : "/devices/details";
    const hasBadLinkId = linkIdParam != null && !isValidLinkId(linkIdParam);
    const wrongBase = location.pathname !== desiredPath;

    if (wrongBase || hasBadLinkId) {
      navigate(
        buildDeviceDetailsPath({
          serialNumber,
          linkId,
          isAdminView: adminView,
        }),
        { replace: true },
      );
    }
  }, [
    serialNumber,
    linkId,
    linkIdParam,
    adminView,
    location.pathname,
    navigate,
  ]);

  if (!serialNumber) return null;

  return <DeviceDetails serialNumber={serialNumber} linkId={linkId} />;
};

interface DevicesRoutesProps {
  isAdminView?: boolean;
}

const DevicesRoutes: React.FC<DevicesRoutesProps> = ({
  isAdminView = false,
}) => {
  return (
    <Routes>
      <Route index element={<DevicesListPage isAdminView={isAdminView} />} />
      <Route
        path="/details"
        element={<DeviceDetailsWrapper isAdminView={isAdminView} />}
      />
    </Routes>
  );
};

export default DevicesRoutes;
